import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import Button from 'components/commercetools-ui/atoms/button';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Rating from 'components/commercetools-ui/atoms/rating';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { trackEvent } from 'helpers/analytics';
import { PLP_PRODUCT_ADDED_TO_CART } from 'helpers/constants/events';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { Cart, ShippingMethod } from 'types/entity/cart';
import { Product, Variant } from 'types/entity/product';
import { LineItem, Wishlist } from 'types/entity/wishlist';
import useTrack from './useTrack';

export interface ProductTileProps {
  product: Product;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  selectedVariantIndex?: number;
  wishlist?: Wishlist;
  cart?: Cart;
  shippingMethods?: ShippingMethod[];
  onClick?: () => void;
  removeLineItem?: (item: LineItem) => Promise<void>;
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
  onAddToCart?: (variant: Variant, quantity: number) => Promise<void>;
}

const ProductTile: FC<ProductTileProps> = ({
  product,
  isSearchResult = false,
  disableVariants = false,
  selectedVariantIndex = 0,
  onClick,
  onAddToCart,
}) => {
  const { ref } = useTrack({ product });
  const translate = useTranslations();
  const { show, fetchRelatedProducts } = useAddToCartOverlay();

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;
  const [userSelectedVariant, setUserSelectedVariant] = useState<Variant | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);

  const selectedVariant = userSelectedVariant ?? variantWithDiscount ?? product?.variants[selectedVariantIndex];

  const productUrl = useMemo(() => {
    if (!product._url) return `/${(product.name ?? '').toLowerCase().replace(/\s+/g, '-')}/p/${selectedVariant.sku}`;
    const name = product._url.split('/')[1];
    return `/${name}/p/${selectedVariant.sku}` + (isSearchResult ? '?sr=1' : '');
  }, [product, selectedVariant, isSearchResult]);

  useEffect(() => {
    fetchRelatedProducts(product);
  }, [product, fetchRelatedProducts]);

  const handleAddToCart = async () => {
    if (!selectedVariant?.isOnStock) return;

    const quantity = 1;

    setLoading(true);

    try {
      await onAddToCart?.(selectedVariant, quantity);

      setAdded(true);
      setTimeout(() => setAdded(false), 1000);

      show({ ...product, _url: productUrl } as Product, selectedVariant, quantity);

      trackEvent(PLP_PRODUCT_ADDED_TO_CART, {
        product,
        variant: selectedVariant,
        quantity,
      });
    } finally {
      setLoading(false);
    }
  };

  // Precio formateado
  const priceFormatted = useMemo(() => {
    const p = selectedVariant?.price;
    if (!p?.centAmount) return null;
    const amount = (p.centAmount / Math.pow(10, p.fractionDigits ?? 2)).toFixed(0);
    return `${p.currencyCode === 'USD' ? '$' : p.currencyCode}${amount}`;
  }, [selectedVariant]);

  // Precio con descuento formateado
  const discountedPriceFormatted = useMemo(() => {
    const dp = selectedVariant?.discountedPrice?.value;
    if (!dp?.centAmount) return null;
    const amount = (dp.centAmount / Math.pow(10, 2)).toFixed(2);
    return `${dp.currencyCode === 'USD' ? '$' : dp.currencyCode}${amount}`;
  }, [selectedVariant]);

  // Features desde attributes.details (JSON: { "Feature Name": "iconUrl" })
  const features = useMemo<[string, string][]>(() => {
    try {
      const raw = selectedVariant?.attributes?.details as string | undefined;
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, string>;
        return Object.entries(parsed);
      }
    } catch {}
    return [];
  }, [selectedVariant]);

  // Descripción: primer párrafo sin HTML
  const plainDescription = useMemo(() => {
    if (!product.description) return '';
    const firstPara = product.description.match(/<p>(.*?)<\/p>/)?.[1];
    return (firstPara ?? product.description).replace(/<[^>]*>/g, '').trim();
  }, [product.description]);

  // Tag badge (ej. "Sale")
  const tagName = selectedVariant?.attributes?.tagName as string | undefined;
  const tagColor = (selectedVariant?.attributes?.tagColor as string | undefined)?.replace(/_/g, ' ');

  // Variantes únicas por color (usando colorName)
  const colorVariants = useMemo(() => {
    const seen = new Set<string>();
    return product.variants.filter((v) => {
      const cn = v.attributes?.colorName as string | undefined;
      if (!cn || seen.has(cn)) return false;
      seen.add(cn);
      return true;
    });
  }, [product.variants]);

  // Spec title desde la categoría del producto
  const specTitle = (product.categories as Array<{ name?: string }>)?.[0]?.name;

  // Título y subtítulo separados por "|" (ej. "MG SuperKnit Band | Glade")
  const [productTitle, productSubtitle] = useMemo(() => {
    const parts = (product.name ?? '').split('|').map((s) => s.trim());
    return [parts[0], parts[1]];
  }, [product.name]);

  // Determinar si pertenece a una categoría root de "MEMBERSHIP"
  const isMembershipRoot = useMemo(() => {
    const categories = (product.categories ?? []) as Array<{
      categoryKey?: string;
      parentKey?: string;
      name?: string;
      slug?: string;
      categoryRef?: string;
      depth?: number;
    }>;
    return categories.some((cat) => {
      // Sólo consideramos categorías root (depth === 0)
      if (cat.depth !== 0) return false;

      const key = `${cat.categoryKey ?? ''} ${cat.parentKey ?? ''}`.toLowerCase();
      const name = (cat.name ?? '').toLowerCase();
      const slug = (cat.slug ?? '').toLowerCase();
      const ref = (cat.categoryRef ?? '').toLowerCase();

      return key.includes('membership') || name === 'membership' || slug === 'membership' || ref === 'membership';
    });
  }, [product.categories]);

  // Layout tipo hero (ONE / LIFE) para memberships
  const renderMembershipTile = () => {
    const attributes = (selectedVariant?.attributes ?? {}) as Record<string, unknown>;

    const namePicture = attributes.namePicture as string | undefined;
    const cardPicture = attributes.cardPicture as string | undefined;
    const productBackground =
      (attributes.productBackground as string | undefined) ??
      'radial-gradient(145.07% 141.42% at 100% 0%, #B7E6DB80 0%, #F6FEECCD 57.37%, #BDB0BE66 100%), #FFF';
    const techDescription = attributes.techDescription as string | undefined;
    const rawSpecTitle = attributes.specTitle as string | null | undefined;
    const membershipSpecTitle = rawSpecTitle != null && rawSpecTitle.trim() !== '' ? rawSpecTitle : undefined;

    const specsFromArray = (attributes.specs as string[] | undefined) ?? [];
    const membershipSpecs = specsFromArray.length ? specsFromArray : features.map(([name]) => name);

    const description = techDescription ?? plainDescription;

    return (
      <div
        className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200 pb-15 pt-40 shadow-lg"
        style={{ background: productBackground }}
      >
        {/* Tag badge */}
        {tagName && tagColor && (
          <div className="flex justify-end px-16 pt-12">
            <span
              className="rounded-full px-12 py-4 text-10 font-bold uppercase tracking-widest text-white"
              style={{ background: tagColor }}
            >
              {tagName}
            </span>
          </div>
        )}

        {/* Nombre / logo */}
        <div className={`px-20 text-center ${tagName ? 'pt-4' : 'pt-20'}`}>
          {namePicture ? (
            <div className="flex justify-center">
              <img src={namePicture} alt={productTitle ?? product.name ?? ''} className="h-32 w-auto" />
            </div>
          ) : (
            <>
              <h2 className="text-22 font-black uppercase tracking-[0.15em] text-neutral-900">{productTitle}</h2>
              {productSubtitle && (
                <p className="mt-2 text-12 font-medium uppercase tracking-widest text-neutral-500">{productSubtitle}</p>
              )}
            </>
          )}
        </div>

        {/* Descripción */}
        {description && (
          <p className="mt-8 line-clamp-3 h-60 px-24 text-center text-16 font-normal leading-5 text-black">
            {description}
          </p>
        )}

        {/* Imagen del producto */}
        <Link link={productUrl}>
          <div className="relative mx-auto mt-16 w-full px-12" onClick={onClick}>
            <div className="relative -mb-30 w-full" style={{ paddingBottom: '75%' }}>
              <Image
                src={cardPicture ?? selectedVariant.images?.[0]}
                alt={product.name ?? ''}
                style={{ objectFit: 'contain', objectPosition: 'center' }}
                fill
              />
            </div>
          </div>
        </Link>

        {/* Selectores de color */}
        {!disableVariants && colorVariants.length > 1 && (
          <div className="mt-12 flex justify-center gap-10 px-16">
            {colorVariants.map((variant, index) => {
              const colorRef = variant.attributes?.colorReference as string | undefined;
              const colorNameAttr = variant.attributes?.colorName as string | undefined;
              const isSelected = variant.sku === selectedVariant.sku;
              return (
                <button
                  key={index}
                  onClick={() => setUserSelectedVariant(variant)}
                  title={colorNameAttr}
                  className={`size-24 overflow-hidden rounded-full border-2 transition-transform ${
                    isSelected ? 'scale-125 border-neutral-800' : 'border-neutral-300 hover:scale-110'
                  }`}
                >
                  {colorRef ? (
                    <img src={colorRef} alt={colorNameAttr ?? ''} className="size-full object-cover" />
                  ) : (
                    <span className="block size-full rounded-full bg-neutral-300" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Barra de precio */}
        <div className="mt-16 border-y border-neutral-200 bg-[#ffffff66] px-20 py-10 text-center">
          {discountedPriceFormatted ? (
            <span className="text-14 font-bold text-black">
              {'Available at '}
              <span className="">{discountedPriceFormatted}</span>{' '}
              <span className="text-12 text-neutral-400 line-through">{priceFormatted}</span>
            </span>
          ) : (
            <span className="text-14 font-bold text-black">
              {'Available at '}
              <span className="">{priceFormatted}</span>
            </span>
          )}
        </div>

        {/* Bloque de specs */}
        {(membershipSpecTitle || membershipSpecs.length > 0) && (
          <div className="mx-16 mt-14 rounded-2xl px-16 py-14 text-16 font-medium leading-6 text-black">
            {membershipSpecTitle && <span>{membershipSpecTitle}</span>}

            {membershipSpecs.length > 0 && (
              <ul className="mt-8 space-y-6">
                {membershipSpecs.map((spec, i) => (
                  <li key={i} className="flex items-center gap-8">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 13 13"
                      fill="none"
                      role="presentation"
                      focusable="false"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="6" cy="6.61328" r="6" fill="black"></circle>
                      <path
                        d="M5.31241 9.39963C5.43526 9.4019 5.55884 9.35617 5.65259 9.26242L9.41959 5.49494C9.60265 5.31189 9.60265 5.01509 9.41959 4.83203L9.08814 4.50057C8.90508 4.31752 8.60828 4.31752 8.42522 4.50058L5.3209 7.6049L4.13166 6.41567C3.9486 6.23261 3.65181 6.23261 3.46875 6.41567L3.13729 6.74712C2.95424 6.93018 2.95424 7.22698 3.13729 7.41003L4.98921 9.26241C5.07864 9.35184 5.19521 9.39758 5.31241 9.39963Z"
                        fill="white"
                      ></path>
                    </svg>
                    <span>{spec}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Botones de acción */}
        <div className="mt-16 space-y-8 px-16 pb-20">
          <button
            className="w-full rounded-2xl bg-black py-14 text-14 font-bold uppercase tracking-[0.2em] text-white transition-colors"
            onClick={async () => await onAddToCart?.(selectedVariant, 1)}
          >
            {`Start with ${productTitle}`}
          </button>
          {/* productUrl → PDP (product detail page) */}
          <Link link={productUrl}>
            <button
              className="mt-10 w-full rounded-2xl border-2 border-neutral-900 bg-white py-14 text-14 font-bold uppercase tracking-[0.2em] text-neutral-900 transition-colors hover:bg-neutral-100"
              onClick={onClick}
              type="button"
            >
              {'Learn More'}
            </button>
          </Link>
        </div>
      </div>
    );
  };

  // Layout tipo tarjeta clásica de catálogo (como la captura que enviaste)
  const renderClassicTile = () => {
    const rating = (selectedVariant?.attributes?.stars as number | undefined) ?? 0;
    const reviews = (selectedVariant?.attributes?.reviews as number | undefined) ?? 0;

    return (
      <Link link={productUrl}>
        <div onClick={onClick} className="flex h-full flex-col overflow-hidden">
          {/* Imagen con botón de Add to Cart al hacer hover */}
          <div className="group relative w-full rounded-3xl bg-neutral-50" style={{ paddingBottom: '90%' }}>
            <Image
              src={selectedVariant.images?.[0]}
              alt={product.name ?? ''}
              style={{ objectFit: 'contain', objectPosition: 'center', scale: '0.75' }}
              fill
            />

            {/* Tag badge */}
            {tagName && tagColor && (
              <div className="absolute left-20 top-20">
                <span
                  className="rounded-full px-16 py-8 text-12 font-bold uppercase tracking-widest text-white"
                  style={{ background: tagColor }}
                >
                  {tagName}
                </span>
              </div>
            )}

            {/* Overlay del botón Add to Cart (solo franja inferior) */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-white/90 p-14 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                className="pointer-events-auto w-full rounded-2xl border-2 border-black bg-transparent text-11 font-black uppercase tracking-[0.2em] text-black hover:border-black hover:bg-transparent"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddToCart();
                }}
                loading={loading}
                added={added}
                disabled={!selectedVariant?.isOnStock}
              >
                {selectedVariant?.isOnStock ? translate('cart.cart-add') : translate('product.currently-unavailable')}
              </Button>
            </div>
          </div>

          {/* Contenido */}
          <div className="flex flex-1 flex-col pb-16 pt-8">
            {/* Nombre */}
            <div className="text-18 font-bold leading-6 text-black">{productTitle}</div>

            {/* Precio */}
            <div className="mt-4 text-18 font-bold leading-6 text-black">{priceFormatted}</div>

            {/* Rating */}
            {rating > 0 && (
              <div className="mt-4">
                <Rating value={rating} reviews={reviews} showValue showReviews size="size-16" textSize="text-14" />
              </div>
            )}

            {/* Swatches de color */}
            {!disableVariants && colorVariants.length > 1 && (
              <div className="mt-10 flex items-center gap-8">
                {colorVariants.map((variant, index) => {
                  const colorRef = variant.attributes?.colorReference as string | undefined;
                  const colorNameAttr = variant.attributes?.colorName as string | undefined;
                  const isSelected = variant.sku === selectedVariant.sku;
                  return (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.preventDefault();
                        setUserSelectedVariant(variant);
                      }}
                      title={colorNameAttr}
                      className={`flex items-center justify-center rounded-full ${isSelected ? 'size-40 border-2 border-neutral-900' : 'size-32'}`}
                    >
                      <span
                        className={`flex size-32 items-center justify-center rounded-full ${isSelected ? 'bg-white' : ''}`}
                      >
                        {colorRef ? (
                          <img
                            src={colorRef}
                            alt={colorNameAttr ?? ''}
                            className="size-full rounded-full object-cover"
                          />
                        ) : (
                          <span className="block size-full rounded-full bg-neutral-300" />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div ref={ref} data-testid="product-tile">
      {isMembershipRoot ? renderMembershipTile() : renderClassicTile()}
    </div>
  );
};

export default ProductTile;
